import { useBackInStockModal } from "/back-in-stock";
import { delay, isEmail, serializeForm, shortUUID } from "/utils";
import { ProductBlocksBuy_buttons } from "_types";
import { cart } from "cart";
import clsx from "clsx";
import { useCartDrawer, useGlobalSettings } from "global-stores";
import { LoadingSpinner } from "loading-spinner";
import { ProductGroupStepType, ReactProductState } from "product";
import { FC, useCallback, useMemo, useState } from "react";
import { StoreApi, UseBoundStore } from "zustand";
import { CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY, CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY, CONFIGURABLE_PRODUCTS_LINE_ITEM_IS_CONFIGURABLE_KEY, CONFIGURABLE_PRODUCTS_LINE_ITEM_PARENT_LIST_KEY } from "./product-configurable-products.constants";
import { getVariantDisplayTitle } from "./variant-helpers";

export const ProductBuyButtons: FC<{
  settings?: ProductBlocksBuy_buttons["settings"];
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
}> = ({ useProduct, settings }) => {
  const { product, productSection, selectedVariant, quantity, formId, selectedSellingPlan, selectedConfigurableProducts, selectedProductExtraOptions, productGroupsSteps } =
    useProduct(
      ({ product, productSection, selectedVariant, quantity, formId, selectedSellingPlan, selectedConfigurableProducts, selectedProductExtraOptions, productGroupsSteps }) => ({
        product,
        productSection,
        selectedVariant,
        quantity,
        formId,
        selectedSellingPlan,
        selectedConfigurableProducts,
        selectedProductExtraOptions,
        productGroupsSteps
      })
    );

  const {
    product_data__preorder_metafield,
    product_data__preorder_date_metafield,
    back_in_stock__enabled,
    back_in_stock__cta,
  } = useGlobalSettings();

  const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
  const [date_ns, date_key] = product_data__preorder_date_metafield.split(".");

  const pre_order =
    selectedVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
    product?.metafields?.[preorder_ns]?.[preorder_key];

  const pre_order_date =
    selectedVariant?.metafields?.[date_ns]?.[date_key] ??
    product?.metafields?.[date_ns]?.[date_key];

  const { cartData, routeProtectionProducts } = useCartDrawer(
    ({ cartData, routeProtectionProducts }) => ({ cartData, routeProtectionProducts })
  );
  const { product_data__preorder_cta } = useGlobalSettings();

  const inCartItem = cartData?.items?.find(
    (line_item) => line_item.variant_id === selectedVariant.id
  );
  const routeProtection = inCartItem
    ? routeProtectionProducts.find((block) => block?.products?.[0]?.handle === inCartItem?.handle)
    : null;

  const isSingleQuantityRouteProtectionItem = !!routeProtection?.limit_quantity;
  const isConfigurableProduct = selectedConfigurableProducts.length > 0;
  
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setLoading(true);
      const configurableProductUUID = shortUUID();

      const giftCardRecipientEmail = productSection.querySelector<HTMLInputElement>(
        "[data-gift-card-recipient-email]"
      );
      const giftCardRecipientCheckbox = productSection.querySelector<HTMLInputElement>(
        "[data-gift-card-recipient-checkbox]"
      );
      const giftCardRecipientEmailError = productSection.querySelector(
        "[data-gift-card-recipient-email-error]"
      );

      if (giftCardRecipientCheckbox?.checked && !isEmail(giftCardRecipientEmail?.value)) {
        giftCardRecipientEmailError?.classList?.remove("hidden");
        setLoading(false);
        return;
      }

      if (
        // @ts-ignore
        window?.Shoppad?.apps?.infiniteoptions?.priceMod?.addToCart &&
        // @ts-ignore
        window?.Shoppad?.apps?.infiniteoptions?.priceMod?.getVariantSubmissions()?.length
      ) {
        // @ts-ignore
        window?.Shoppad?.apps?.infiniteoptions?.priceMod?.addToCart(e);
        await delay(500);
      }

      const properties = {};
      Object.entries(serializeForm(e.target))?.forEach(([key, value]) => {
        if (key.includes("properties[") && value) {
          properties[key.replace(/^properties\[(.*)]$/gi, "$1")] = value;
        }
      });

      if (isConfigurableProduct) {
        properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY] = configurableProductUUID;
      }
      
      if (!selectedVariant.available && back_in_stock__enabled) {
        useBackInStockModal.setState({
          open: true,
          variant_id: selectedVariant?.id,
          product: product,
        });
        setLoading(false);
        return;
      }

      if (pre_order && selectedVariant?.inventory_quantity < quantity) {
        const now_date = Date.now();
        let label = "On Preorder";
        let timeOfMonth = "early ";

        if (pre_order_date) {
          const target_date = new Date(pre_order_date as string).getTime();
          const target_day = +new Date(pre_order_date as string).toLocaleString("en", {
            day: "2-digit",
          });
          const target_month = new Date(pre_order_date as string).toLocaleString("en", {
            month: "long",
          });
          const difference = Math.round((target_date - now_date) / 1000 / 60 / 60 / 24);

          if (target_day > 7) {
            timeOfMonth = "mid ";
          }
          if (target_day > 20) {
            timeOfMonth = "end of ";
          }

          label = "Preorder Shipping this week";

          if (difference > 7) {
            label = "Preorder Shipping next week";
          }
          if (difference > 15) {
            label = "Preorder Shipping end of the month";
          }
          if (difference > 31) {
            label = "Preorder Shipping early next month";
          }
          if (difference > 45) {
            label = `Preorder Shipping ${timeOfMonth} ${target_month}`;
          }
        }

        if (selectedVariant?.inventory_quantity > 0) {
          await cart.add(
            {
              items: [
                {
                  id: selectedVariant?.id,
                  quantity: selectedVariant?.inventory_quantity,
                  selling_plan: selectedSellingPlan
                    ? +selectedSellingPlan?.id
                    : product?.requires_selling_plan
                    ? selectedVariant?.selling_plan_allocations?.[0]?.id
                    : undefined,
                  properties,
                },
                {
                  id: selectedVariant?.id,
                  quantity: quantity - selectedVariant?.inventory_quantity,
                  selling_plan: selectedSellingPlan
                    ? +selectedSellingPlan?.id
                    : product?.requires_selling_plan
                    ? selectedVariant?.selling_plan_allocations?.[0]?.id
                    : undefined,
                  properties: {
                    ...properties,
                    preorder: label,
                  },
                },
                ...selectedConfigurableProducts.map((configurableProduct) => {
                  return {
                  id: configurableProduct.selectedVariant.id,
                  quantity: configurableProduct.quantityToAdd,
                  parent_id: selectedVariant?.id, // Use Shopify's native parent_id for nesting
                  properties: {
                    [CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]: configurableProductUUID
                  }
                }})
              ],
            },
            product?.openCartAfterAdd
          );
        }

        if (selectedVariant?.inventory_quantity <= 0) {
          await cart.add(
            {
              items: [
                {
                  id: selectedVariant?.id,
                  quantity: quantity,
                  selling_plan: selectedSellingPlan
                    ? +selectedSellingPlan?.id
                    : product?.requires_selling_plan
                    ? selectedVariant?.selling_plan_allocations?.[0]?.id
                    : undefined,
                  properties: {
                    ...properties,
                    preorder: label,
                  },
                },
                ...selectedConfigurableProducts.map((configurableProduct) => {
                  return {
                  id: configurableProduct.selectedVariant.id,
                  quantity: configurableProduct.quantityToAdd,
                  parent_id: selectedVariant?.id, // Use Shopify's native parent_id for nesting
                  properties: {
                    [CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]: configurableProductUUID
                  }
                }})
              ],
            },
            product?.openCartAfterAdd
          );
        }
        if (!product?.openCartAfterAdd) {
          document.dispatchEvent(new Event(`product:close`));
        }
      }

      if (!pre_order || selectedVariant?.inventory_quantity >= quantity) {
        await cart.add(
          {
            items: [
              {
                id: selectedVariant?.id,
                quantity,
                selling_plan: selectedSellingPlan
                  ? +selectedSellingPlan?.id
                  : product?.requires_selling_plan
                  ? selectedVariant?.selling_plan_allocations?.[0]?.id
                  : undefined,
                properties,
              },
              ...selectedConfigurableProducts.map((configurableProduct) => {
                const childItem = {
                id: configurableProduct.selectedVariant.id,
                quantity: configurableProduct.quantityToAdd,
                parent_id: selectedVariant?.id, // Use Shopify's native parent_id for nesting
                properties: {
                  [CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]: configurableProductUUID
                }
              };
              return childItem;
              })
            ],
          },
          product?.openCartAfterAdd
        );
        if (!product?.openCartAfterAdd) {
          document.dispatchEvent(new Event(`product-quick-view:close`));
        }
      }

      setLoading(false);
    },
    [
      back_in_stock__enabled,
      pre_order,
      pre_order_date,
      product,
      productSection,
      quantity,
      selectedSellingPlan,
      selectedVariant.available,
      selectedVariant?.id,
      selectedVariant?.inventory_quantity,
      selectedVariant?.selling_plan_allocations,
      selectedConfigurableProducts
    ]
  );

  const comparePrice =
    selectedSellingPlan?.compare_at_price ??
    selectedVariant?.compare_at_price ??
    selectedVariant?.price;
  const price = selectedSellingPlan?.price ?? selectedVariant?.price;


  const shouldDisableAddToCart = useMemo(() => {
    if (productGroupsSteps && productGroupsSteps.length > 0) {
      const requiredSteps = productGroupsSteps.filter((step) => step.required);

      if (requiredSteps.length === 0) {
        return false;
      }

      return requiredSteps.some((step) => {
        if (step.sourceType === ProductGroupStepType.SETTING) {
          const selection = selectedConfigurableProducts?.find((p) => p.stepNumber === step.stepNumber)
          return !selection;
        }

        if (step.sourceType === ProductGroupStepType.METAFIELD && step.namespace) {
          const metaSelection = selectedProductExtraOptions && selectedProductExtraOptions[step.namespace];
          return !metaSelection;
        }

        return false;
      });
    }

    return (!selectedVariant?.available && !back_in_stock__enabled) ||
    isSingleQuantityRouteProtectionItem;
  }, [
    selectedVariant,
    back_in_stock__enabled,
    isSingleQuantityRouteProtectionItem,
    productGroupsSteps,
    selectedConfigurableProducts,
    selectedProductExtraOptions
  ]);

  if (!product) {
    return null;
  }

  return (
    <form
      method="post"
      action="/cart/add"
      id={formId}
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate={true}
      onSubmit={handleSubmit}
    >
      <input type="hidden" name="form_type" value="product" />
      <input type="hidden" name="utf8" value="✓" />
      <input type="hidden" name="id" value={selectedVariant?.id} />
      <input
        type="hidden"
        name="selling_plan"
        data-selling-plan-output={true}
        value={selectedSellingPlan?.id}
        disabled={!selectedSellingPlan}
      />

      {isConfigurableProduct && (
        <>
          {selectedProductExtraOptions && Object.entries(selectedProductExtraOptions).map(([key, value]) => {
            return (<input type="hidden" name={`properties[${key.replace("_", " ")}]`} value={value.name} />)
          })}
          {selectedConfigurableProducts && selectedConfigurableProducts.map((p) => {
            const displayTitle = getVariantDisplayTitle(p.product, p.selectedVariant);
            return (<input type="hidden" name={`properties[_${p.lineItemLabel}]`} value={displayTitle} />)
          })}
          <input type="hidden" name={`properties[${CONFIGURABLE_PRODUCTS_LINE_ITEM_IS_CONFIGURABLE_KEY}]`} value={"1"} />
          
        </>
      )}

      

      <button
        type="submit"
        name="add"
        disabled={
          shouldDisableAddToCart
        }
        className={clsx(
          "w-full",
          settings?.button__style ?? "button-primary",
          settings?.button__size ?? "button--md"
        )}
      >
        <span>
          {!selectedVariant
            ? window.theme_content?.product?.unavailable
            : selectedVariant?.available
            ? pre_order && product_data__preorder_cta && selectedVariant?.inventory_quantity <= 0
              ? window.theme_content?.product?.pre_order
              : window.theme_content?.product?.add_to
            : back_in_stock__enabled
            ? back_in_stock__cta
            : window.theme_content?.product?.sold_out}
        </span>
        {settings?.button__show_price
          ? comparePrice > price && settings.button__show_compare_price
            ? <span className="inline-flex items-center gap-1">
                <span
                  className="line-through opacity-60"
                  dangerouslySetInnerHTML={{ __html: window.formatMoney(comparePrice) }}
                ></span>
                <span dangerouslySetInnerHTML={{ __html: window.formatMoney(price) }}></span>
              </span>
            : <span dangerouslySetInnerHTML={{ __html: window.formatMoney(price) }}></span>
          : null}
        <LoadingSpinner loading={loading} />
      </button>
    </form>
  );
};
